/**
 * The duelling hall — a gothic cloister built procedurally.
 *
 * Geometry is instanced wherever it repeats (pillars, arches, ribs, candles),
 * materials are shared, and everything is static, so the whole arena costs a
 * handful of draw calls. Detail scales with the graphics preset without
 * changing anything the player needs to read during a fight.
 */
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { MeshReflectorMaterial } from '@react-three/drei';
import { useSettings } from '../gameplay/settingsStore';
import { bannerTexture, glowTexture, grungeTexture, runeRingTexture, stoneRoughness, stoneTexture } from '../assets/textures';

const HALL_LENGTH = 46;
const HALL_WIDTH = 19;
const HALL_HEIGHT = 15;
const PILLAR_X = 7.6;
const PILLAR_SPACING = 6.4;

function usePillarPositions() {
  return useMemo(() => {
    const out: [number, number][] = [];
    for (let z = -HALL_LENGTH / 2 + 3; z <= HALL_LENGTH / 2 - 3; z += PILLAR_SPACING) {
      out.push([-PILLAR_X, z], [PILLAR_X, z]);
    }
    return out;
  }, []);
}

/* ------------------------------------------------------------------ */

function Floor() {
  const quality = useSettings((s) => s.quality);
  const map = useMemo(() => {
    const t = stoneTexture();
    t.repeat.set(6, 12);
    return t;
  }, []);
  const rough = useMemo(() => {
    const t = stoneRoughness();
    t.repeat.set(6, 12);
    return t;
  }, []);

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
      <planeGeometry args={[HALL_WIDTH + 4, HALL_LENGTH]} />
      {quality.reflections ? (
        <MeshReflectorMaterial
          map={map}
          roughnessMap={rough}
          color="#8d8f9c"
          mixBlur={1.1}
          mixStrength={2.4}
          resolution={quality.shadowMapSize >= 2048 ? 1024 : 512}
          blur={[320, 90]}
          depthScale={1.1}
          minDepthThreshold={0.3}
          maxDepthThreshold={1.2}
          metalness={0.22}
          roughness={0.72}
          mirror={0.35}
        />
      ) : (
        <meshStandardMaterial map={map} roughnessMap={rough} color="#7f818c" metalness={0.1} roughness={0.85} />
      )}
    </mesh>
  );
}

/* ------------------------------------------------------------------ */

function Pillars() {
  const quality = useSettings((s) => s.quality);
  const positions = usePillarPositions();
  const ref = useRef<THREE.InstancedMesh>(null);
  const map = useMemo(() => {
    const t = grungeTexture();
    t.repeat.set(1, 4);
    return t;
  }, []);

  useMemo(() => {
    if (!ref.current) return;
  }, []);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame(() => {
    const mesh = ref.current;
    if (!mesh || mesh.userData.placed) return;
    positions.forEach(([x, z], i) => {
      dummy.position.set(x, HALL_HEIGHT / 2, z);
      dummy.rotation.set(0, (i % 2) * 0.2, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.userData.placed = true;
  });

  return (
    <>
      <instancedMesh ref={ref} args={[undefined, undefined, positions.length]} castShadow receiveShadow frustumCulled>
        <cylinderGeometry args={[0.62, 0.78, HALL_HEIGHT, Math.max(8, quality.geometryDetail), 1]} />
        <meshStandardMaterial map={map} color="#3f4352" roughness={0.92} metalness={0.05} />
      </instancedMesh>
      {/* Capitals and bases: cheap, and they sell the silhouette. */}
      {positions.map(([x, z], i) => (
        <group key={`cap-${i}`}>
          <mesh position={[x, HALL_HEIGHT - 0.4, z]} castShadow>
            <boxGeometry args={[1.7, 0.8, 1.7]} />
            <meshStandardMaterial color="#4c5062" roughness={0.86} />
          </mesh>
          <mesh position={[x, 0.35, z]} receiveShadow>
            <boxGeometry args={[1.9, 0.7, 1.9]} />
            <meshStandardMaterial color="#383b49" roughness={0.92} />
          </mesh>
        </group>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Arches() {
  const quality = useSettings((s) => s.quality);
  const positions = usePillarPositions();
  const spans = useMemo(() => {
    const out: { x: number; z: number }[] = [];
    const zs = [...new Set(positions.map((p) => p[1]))].sort((a, b) => a - b);
    for (let i = 0; i < zs.length - 1; i++) {
      const z = (zs[i] + zs[i + 1]) / 2;
      out.push({ x: -PILLAR_X, z }, { x: PILLAR_X, z });
    }
    return out;
  }, [positions]);

  return (
    <>
      {spans.map((s, i) => (
        <mesh
          key={i}
          position={[s.x, HALL_HEIGHT - 0.6, s.z]}
          rotation={[0, Math.PI / 2, 0]}
          castShadow
        >
          <torusGeometry args={[PILLAR_SPACING / 2, 0.28, 6, Math.max(10, quality.geometryDetail), Math.PI]} />
          <meshStandardMaterial color="#464a5c" roughness={0.9} />
        </mesh>
      ))}
      {/* Cross ribs spanning the vault. */}
      {[...new Set(positions.map((p) => p[1]))].map((z, i) => (
        <mesh key={`rib-${i}`} position={[0, HALL_HEIGHT - 0.4, z]} rotation={[0, 0, 0]}>
          <torusGeometry args={[PILLAR_X + 0.4, 0.22, 6, Math.max(12, quality.geometryDetail), Math.PI]} />
          <meshStandardMaterial color="#3d4152" roughness={0.92} />
        </mesh>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Walls() {
  const map = useMemo(() => {
    const t = grungeTexture();
    t.repeat.set(6, 3);
    return t;
  }, []);
  const material = useMemo(
    () => new THREE.MeshStandardMaterial({ map, color: '#3f4250', roughness: 0.95, side: THREE.FrontSide }),
    [map],
  );

  return (
    <group>
      <mesh position={[-HALL_WIDTH / 2 - 1.2, HALL_HEIGHT / 2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow material={material}>
        <planeGeometry args={[HALL_LENGTH, HALL_HEIGHT]} />
      </mesh>
      <mesh position={[HALL_WIDTH / 2 + 1.2, HALL_HEIGHT / 2, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow material={material}>
        <planeGeometry args={[HALL_LENGTH, HALL_HEIGHT]} />
      </mesh>
      <mesh position={[0, HALL_HEIGHT / 2, -HALL_LENGTH / 2]} receiveShadow material={material}>
        <planeGeometry args={[HALL_WIDTH + 4, HALL_HEIGHT]} />
      </mesh>
      <mesh position={[0, HALL_HEIGHT / 2, HALL_LENGTH / 2]} rotation={[0, Math.PI, 0]} receiveShadow material={material}>
        <planeGeometry args={[HALL_WIDTH + 4, HALL_HEIGHT]} />
      </mesh>
      <mesh position={[0, HALL_HEIGHT, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[HALL_WIDTH + 4, HALL_LENGTH]} />
        <meshStandardMaterial color="#23252f" roughness={1} />
      </mesh>
    </group>
  );
}

/* ------------------------------------------------------------------ */

/** Tall lancet windows with a volumetric-ish shaft, the hall's key light. */
function Windows() {
  const zs = useMemo(() => {
    const out: number[] = [];
    for (let z = -HALL_LENGTH / 2 + 6; z <= HALL_LENGTH / 2 - 6; z += PILLAR_SPACING * 2) out.push(z);
    return out;
  }, []);

  return (
    <>
      {zs.map((z, i) =>
        [-1, 1].map((sx) => (
          <group key={`${i}-${sx}`}>
            <mesh position={[sx * (HALL_WIDTH / 2 + 1.1), 8.2, z]} rotation={[0, sx > 0 ? -Math.PI / 2 : Math.PI / 2, 0]}>
              <planeGeometry args={[2.4, 7]} />
              <meshBasicMaterial color="#ffd9a8" toneMapped={false} />
            </mesh>
            {/* Light shaft: a stretched additive quad angled into the room. */}
            <mesh
              position={[sx * (HALL_WIDTH / 2 - 2.6), 5.4, z]}
              rotation={[0, sx > 0 ? -Math.PI / 2 : Math.PI / 2, sx > 0 ? -0.36 : 0.36]}
            >
              <planeGeometry args={[6.5, 9]} />
              <meshBasicMaterial
                color="#ffcf9a"
                transparent
                opacity={0.022}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
                depthTest={false}
                side={THREE.DoubleSide}
                toneMapped={false}
              />
            </mesh>
          </group>
        )),
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Banners() {
  const tex = useMemo(() => bannerTexture(), []);
  const positions = usePillarPositions().filter((_, i) => i % 2 === 0 || i % 3 === 0);

  return (
    <>
      {positions.map(([x, z], i) => (
        <group key={i} position={[x + (x < 0 ? 0.9 : -0.9), 8.4, z]}>
          <mesh rotation={[0, x < 0 ? Math.PI / 2 : -Math.PI / 2, 0]}>
            <planeGeometry args={[1.6, 4.4, 1, 6]} />
            <meshStandardMaterial map={tex} side={THREE.DoubleSide} roughness={0.85} />
          </mesh>
          <mesh position={[0, 2.35, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.06, 0.06, 1.8, 6]} />
            <meshStandardMaterial color="#8a7a4c" metalness={0.6} roughness={0.4} />
          </mesh>
        </group>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Candles() {
  const quality = useSettings((s) => s.quality);
  const glow = useMemo(() => glowTexture(), []);
  const spots = useMemo(() => {
    const out: [number, number, number][] = [];
    for (let z = -HALL_LENGTH / 2 + 5; z <= HALL_LENGTH / 2 - 5; z += PILLAR_SPACING) {
      out.push([-PILLAR_X + 0.9, 3.2, z], [PILLAR_X - 0.9, 3.2, z]);
    }
    return out;
  }, []);
  const flameRefs = useRef<THREE.Sprite[]>([]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    flameRefs.current.forEach((sprite, i) => {
      if (!sprite) return;
      const flicker = 0.82 + Math.sin(t * 9 + i * 2.1) * 0.1 + Math.sin(t * 23 + i) * 0.06;
      sprite.scale.setScalar(0.9 * flicker);
      (sprite.material as THREE.SpriteMaterial).opacity = 0.75 * flicker;
    });
  });

  return (
    <>
      {spots.map((p, i) => (
        <group key={i} position={p}>
          <mesh>
            <cylinderGeometry args={[0.06, 0.07, 0.34, 6]} />
            <meshStandardMaterial color="#e8dcc0" roughness={0.8} emissive="#4a3418" emissiveIntensity={0.4} />
          </mesh>
          <sprite
            ref={(el) => {
              if (el) flameRefs.current[i] = el;
            }}
            position={[0, 0.32, 0]}
          >
            <spriteMaterial
              map={glow}
              color="#ffb15e"
              transparent
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              toneMapped={false}
            />
          </sprite>
          {i < quality.candleLights ? (
            <pointLight color="#ff9d4a" intensity={5} distance={7} decay={2} position={[0, 0.4, 0]} />
          ) : null}
        </group>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ */

/** The duelling sigil inlaid in the floor between the two mages. */
function FloorSigil() {
  const tex = useMemo(() => runeRingTexture(2), []);
  const ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (ref.current) ref.current.rotation.z = state.clock.elapsedTime * 0.045;
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0]}>
      <planeGeometry args={[9, 9]} />
      <meshBasicMaterial
        map={tex}
        transparent
        opacity={0.16}
        color="#7fb4ff"
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

/* ------------------------------------------------------------------ */

/** Floating motes. Skipped entirely on the low preset. */
function Dust() {
  const count = useSettings((s) => s.quality.dustParticles);
  const ref = useRef<THREE.Points>(null);
  const glow = useMemo(() => glowTexture(), []);

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const positions = new Float32Array(Math.max(1, count) * 3);
    const speeds = new Float32Array(Math.max(1, count));
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * HALL_WIDTH;
      positions[i * 3 + 1] = Math.random() * 9 + 0.4;
      positions[i * 3 + 2] = (Math.random() - 0.5) * HALL_LENGTH * 0.7;
      speeds[i] = 0.08 + Math.random() * 0.16;
    }
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.userData.speeds = speeds;
    return g;
  }, [count]);

  useFrame((_, delta) => {
    if (count === 0 || !ref.current) return;
    const attr = ref.current.geometry.getAttribute('position') as THREE.BufferAttribute;
    const speeds = ref.current.geometry.userData.speeds as Float32Array;
    const arr = attr.array as Float32Array;
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 1] += speeds[i] * delta;
      if (arr[i * 3 + 1] > 9.6) arr[i * 3 + 1] = 0.3;
    }
    attr.needsUpdate = true;
  });

  if (count === 0) return null;

  return (
    <points ref={ref} geometry={geometry} frustumCulled={false}>
      <pointsMaterial
        size={0.07}
        map={glow}
        color="#cfe0ff"
        transparent
        opacity={0.35}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        toneMapped={false}
      />
    </points>
  );
}

/* ------------------------------------------------------------------ */

export function Arena() {
  return (
    <group>
      <Floor />
      <Walls />
      <Pillars />
      <Arches />
      <Windows />
      <Banners />
      <Candles />
      <FloorSigil />
      <Dust />
    </group>
  );
}

export const ARENA_DIMENSIONS = { HALL_LENGTH, HALL_WIDTH, HALL_HEIGHT };
